/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'src/utils/dayjs';
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import ResetIcon from '@mui/icons-material/RestartAlt';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import FILTER_TYPES from 'src/utils/constants/filter';
import useStyles from './styles';

const toMomentOrNull = value => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  const m = dayjs(value);
  return m.isValid() ? m : null;
};

const makeInitialState = currentFilterValues => {
  const filterValuesProps = Object.keys(currentFilterValues);
  const initialState = {};
  filterValuesProps.forEach(value => {
    initialState[value] = currentFilterValues[value] || false;
  });

  return initialState;
};

const Filter = ({
  anchorEl,
  handleClose,
  filterValues,
  filters,
  handleChange,
  allowManualInput = false
}) => {
  const { classes } = useStyles();
  const resetRef = useRef();
  const [state, setState] = useState(makeInitialState(filterValues));
  const { t } = useTranslation();

  const handleChangeFilter = event => {
    if (event.target.type === 'radio') {
      setState({ ...state, [event.target.name]: event.target.value });
      handleChange({ ...state, [event.target.name]: event.target.value });
    } else if (event.target.type === 'checkbox') {
      setState({ ...state, [event.target.name]: event.target.checked });
      handleChange({ ...state, [event.target.name]: event.target.checked });
    }
  };

  const handleChangeDateFilter = useCallback(
    (event, value) => {
      setState(prevState => {
        const newState = { ...prevState, [value]: event };
        handleChange(newState);
        return newState;
      });
    },
    [handleChange, setState]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, anchorEl]);

  return (
    <Box className={classes.root}>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Container className={classes.horizontalPadding}>
          {filters.map((filter, index) => (
            <Box key={index} display="flex" flexDirection="column">
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.typography}
              >
                {t(filter.title).toUpperCase()}
              </Typography>
              {filter.controlGroup === FILTER_TYPES.CHECKBOX &&
                filter.items.map(item => (
                  <FormControlLabel
                    key={item.value}
                    control={
                      <Checkbox
                        name={item.value}
                        color="primary"
                        value={filterValues[item.value]}
                        checked={filterValues[item.value]}
                        onChange={handleChangeFilter}
                      />
                    }
                    label={t(item.label)}
                  />
                ))}

              {filter.controlGroup === FILTER_TYPES.RADIO_BUTTON && (
                <RadioGroup
                  display="flex"
                  flexDirection="column"
                  value={filterValues[filter.value]}
                  onChange={handleChangeFilter}
                  name={filterValues[filter.value]}
                >
                  {filter.items.map(item => (
                    <FormControlLabel
                      key={item.value}
                      control={
                        <Radio
                          name={filter.value}
                          color="primary"
                          checked={filterValues[filter.value] === item.value}
                        />
                      }
                      value={item.value}
                      label={t(item.label)}
                    />
                  ))}
                </RadioGroup>
              )}

              {filter.controlGroup === FILTER_TYPES.DATE &&
                filter.items.map(item => (
                  <Box key={item.value} sx={{ display: 'flex', flexDirection: 'row' }}>
                    <DatePicker
                      label={t(item.label)}
                      value={toMomentOrNull(filterValues[item.value])}
                      locale="en"
                      views={item.view}
                      minDate={dayjs.tz().subtract(5, 'years')}
                      onChange={event =>
                        handleChangeDateFilter(event, item.value)
                      }
                      slotProps={{
                        textField: {
                          sx: { width: '100%', margin: '16px 0' },
                          inputProps: {
                            readOnly: Boolean(!allowManualInput)
                          },
                          InputProps: {
                            autoComplete: 'off'
                          },
                          onClick: e => {
                            if (!allowManualInput) {
                              e.stopPropagation();
                            }
                          },
                          onFocus: e => {
                            if (!allowManualInput) {
                              e.target.blur();
                            }
                          },
                          onKeyDown: e => {
                            if (!allowManualInput) {
                              e.preventDefault();
                            }
                          }
                        }
                      }}
                    />
                    {filterValues[item.value] &&
                      filterValues[item.value] !== dayjs.tz() && (
                        <IconButton
                          aria-label="reset"
                          size="large"
                          sx={{ alignSelf: 'center' }}
                          onClick={() =>
                            handleChangeDateFilter(null, item.value)
                          }
                        >
                          <ResetIcon />
                        </IconButton>
                      )}
                  </Box>
                ))}

              {filter.controlGroup === FILTER_TYPES.DATE_IN_RANGE && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  {filter.items.map(item => {
                    return (
                      <Box
                        key={item.value}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center'
                        }}
                      >
                        <DatePicker
                          label={t(item.label)}
                          value={toMomentOrNull(filterValues[item.value])}
                          views={item.view}
                          minDate={
                            item.value === 'endDate' && filterValues.startDate
                              ? toMomentOrNull(filterValues.startDate)
                              : null
                          }
                          maxDate={
                            item.value === 'startDate' && filterValues.endDate
                              ? toMomentOrNull(filterValues.endDate)
                              : null
                          }
                          onChange={event => {
                            handleChangeDateFilter(event, item.value);
                          }}
                          slotProps={{
                            textField: {
                              inputProps: {
                                readOnly: Boolean(!allowManualInput)
                              },
                              InputProps: {
                                autoComplete: 'off'
                              },
                              onClick: e => {
                                if (!allowManualInput) {
                                  e.stopPropagation();
                                }
                              },
                              onFocus: e => {
                                if (!allowManualInput) {
                                  e.target.blur();
                                }
                              },
                              onKeyDown: e => {
                                if (!allowManualInput) {
                                  e.preventDefault();
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}
        </Container>
      </Popover>
    </Box>
  );
};

Filter.propTypes = {
  anchorEl: PropTypes.object,
  handleClose: PropTypes.func,
  filterValues: PropTypes.any,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      controlGroup: PropTypes.string,
      value: PropTypes.string, // Only for radio buttons
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any,
          view: PropTypes.any // Only for date
        })
      )
    })
  ),
  handleChange: PropTypes.func,
  allowManualInput: PropTypes.bool
};

export default Filter;
